import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, FileText, ArrowLeft, Download } from 'lucide-react';

const EMPTY = { title: '', abbr: '', category: '', description: '', purpose: '', sections: [], published: true, status: 'Published' };

const SocialWorkFormsAdmin = () => {
  const [data, setData] = useState({ forms: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sectionsText, setSectionsText] = useState('[]');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/admin/social-work-forms`); setData(r.data); }
    catch (e) { toast.error(e.response?.status === 403 ? 'Admin access required.' : 'Could not load.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Social Work Forms Admin | INASW'; load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setSectionsText('[\n  {"heading": "Section", "fields": [{"label": "Field", "kind": "text"}]}\n]'); setOpen(true); };
  const openEdit = (f) => { setEditing(f.form_id); setForm({ ...EMPTY, ...f }); setSectionsText(JSON.stringify(f.sections || [], null, 2)); setOpen(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.category) { toast.error('Title and category are required.'); return; }
    let sections;
    try { sections = JSON.parse(sectionsText || '[]'); if (!Array.isArray(sections)) throw new Error(); }
    catch (e) { toast.error('Sections must be valid JSON array.'); return; }
    setSaving(true);
    const payload = { ...form, sections }; delete payload.form_id; delete payload._id; delete payload.created_at; delete payload.category_label;
    try {
      if (editing) await axios.put(`${API_URL}/admin/social-work-forms/${editing}`, payload);
      else await axios.post(`${API_URL}/admin/social-work-forms`, payload);
      toast.success(editing ? 'Updated.' : 'Form added.'); setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); } finally { setSaving(false); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.title}"?`)) return;
    try { await axios.delete(`${API_URL}/admin/social-work-forms/${f.form_id}`); toast.success('Deleted.'); await load(); }
    catch (e) { toast.error('Could not delete.'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-sm text-gray-500 hover:text-primary flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Admin Dashboard</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><FileText className="w-4 h-4" /> Directory Management</p>
            <h1 className="font-serif text-3xl font-bold text-primary" data-testid="swf-admin-title">Social Work Forms</h1>
          </div>
          <Button className="bg-primary text-white" onClick={openNew} data-testid="swf-admin-add"><Plus className="w-4 h-4 mr-1" /> Add Form</Button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="bg-white rounded-2xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr>
                <th className="px-4 py-3">Form</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {data.forms.map((f) => (
                  <tr key={f.form_id} className="border-t" data-testid={`swf-admin-row-${f.form_id}`}>
                    <td className="px-4 py-3"><span className="font-medium text-gray-800">{f.title}</span> {f.abbr && <span className="text-xs text-secondary font-semibold">({f.abbr})</span>}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{data.categories.find((c) => c.slug === f.category)?.label || f.category}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{f.status}{f.published ? '' : ' (hidden)'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <a href={`${API_URL}/social-work-forms/${f.form_id}/pdf`} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-primary p-1 inline-block"><Download className="w-4 h-4" /></a>
                      <button onClick={() => openEdit(f)} className="text-gray-400 hover:text-primary p-1" data-testid={`swf-edit-${f.form_id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(f)} className="text-gray-400 hover:text-red-600 p-1" data-testid={`swf-delete-${f.form_id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {data.forms.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No forms.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Form' : 'Add Form'}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setF('title', e.target.value)} data-testid="swf-form-title" /></div>
            <div><Label className="text-xs">Abbreviation</Label><Input value={form.abbr} onChange={(e) => setF('abbr', e.target.value)} /></div>
            <div><Label className="text-xs">Category *</Label>
              <select value={form.category} onChange={(e) => setF('category', e.target.value)} className="w-full border rounded-md h-10 px-2 text-sm" data-testid="swf-form-category">
                <option value="">Select…</option>
                {data.categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setF('description', e.target.value)} data-testid="swf-form-description" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Purpose</Label><Textarea rows={2} value={form.purpose} onChange={(e) => setF('purpose', e.target.value)} /></div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Sections (JSON) — each: {'{'}"heading","fields":[{'{'}"label","kind":"text|multiline|date|checkbox","options":[]{'}'}]{'}'}</Label>
              <Textarea rows={8} value={sectionsText} onChange={(e) => setSectionsText(e.target.value)} className="font-mono text-xs" data-testid="swf-form-sections" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="pub" checked={form.published} onCheckedChange={(v) => setF('published', !!v)} data-testid="swf-form-published" />
              <Label htmlFor="pub" className="text-xs text-gray-600">Published (visible to public)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={save} disabled={saving} data-testid="swf-form-save">{saving ? 'Saving…' : (editing ? 'Save changes' : 'Add form')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialWorkFormsAdmin;
